/**
 * Popover — a card of facts about something named on the page, opened in place.
 *
 * The item page names cycles, tracks and sessions; this is how a person reads
 * what one of them holds without leaving the page. It is not a Tooltip: a
 * tooltip is one short line, never holds a link, and vanishes the moment the
 * pointer leaves the control. A popover holds rows and a link, and stays open
 * while the pointer travels into it.
 *
 *   <Popover label="Session summary" content={<PopoverRows rows={…} />}>
 *     <a href="…">Planning offsite</a>
 *   </Popover>
 *
 * Most callers never write this: `<ObjectRef … summary={…} />` wraps itself.
 *
 * How it opens and closes:
 *   · pointer resting on the trigger (a short delay, so passing over does nothing)
 *   · keyboard focus arriving inside the trigger
 *   · a `PopoverToggle` inside the trigger — the only way on a touch screen
 *   · it stays open while the pointer is over the card; it closes when the
 *     pointer leaves both, on Escape, when focus leaves both, or on a press
 *     outside.
 * A link inside the trigger is left alone: clicking it still opens the page.
 *
 * The card is portaled to <body> with fixed position, so a clipped or scrolling
 * parent cannot cut it off. It sits below the trigger and flips above when
 * there is no room, and it is kept 16px inside the screen on both sides.
 */
import {
  Fragment,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { FOCUS_RING, LABEL_MONO } from '../mono/primitives';

const OPEN_DELAY_MS = 250;
const CLOSE_DELAY_MS = 150;
const GAP = 6;
const GUTTER = 16;

export interface PopoverProps {
  /** What the card says. Drawn only while open. */
  content: ReactNode;
  /** Names the card for assistive technology: "Session summary". */
  label: string;
  /** The thing the card describes. */
  children: ReactNode;
  /** Card width in px. Never wider than the screen less the gutters. */
  width?: number;
  className?: string;
}

interface PopoverCtx {
  open: boolean;
  toggle: () => void;
  panelId: string;
}

const PopoverContext = createContext<PopoverCtx | null>(null);

export function Popover({ content, label, children, width = 400, className = '' }: PopoverProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; width: number; maxHeight?: number } | null>(null);
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const panelId = useId();

  const clear = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  const later = (fn: () => void, ms: number) => {
    clear();
    timer.current = setTimeout(fn, ms);
  };
  useEffect(() => clear, []);

  const place = useCallback(() => {
    const t = triggerRef.current;
    if (!t) return;
    const r = t.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const w = Math.min(width, vw - GUTTER * 2);
    const h = panelRef.current?.scrollHeight ?? 0;
    const left = Math.max(GUTTER, Math.min(r.left, vw - w - GUTTER));
    const below = r.bottom + GAP;
    const roomBelow = vh - GUTTER / 2 - below;
    const roomAbove = r.top - GAP - GUTTER / 2;
    // Below when it fits, else above when it fits, else on the roomier side
    // with the card scrolling inside — never past the edge of the screen.
    if (h <= roomBelow) setPos({ top: below, left, width: w });
    else if (h <= roomAbove) setPos({ top: r.top - GAP - h, left, width: w });
    else if (roomBelow >= roomAbove) setPos({ top: below, left, width: w, maxHeight: roomBelow });
    else setPos({ top: GUTTER / 2, left, width: w, maxHeight: roomAbove });
  }, [width]);

  // Measure once the card exists (its height decides above or below), and
  // follow the trigger while the page scrolls or resizes.
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    place();
  }, [open, place]);
  useEffect(() => {
    if (!open) return;
    const onMove = () => place();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const hadFocus = panelRef.current?.contains(document.activeElement);
      setOpen(false);
      if (hadFocus) triggerRef.current?.querySelector<HTMLElement>('a,button,[tabindex]')?.focus();
    };
    const onDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    // The card's own height changes when what it shows finishes loading.
    const ro = typeof ResizeObserver !== 'undefined' && panelRef.current ? new ResizeObserver(onMove) : null;
    if (ro && panelRef.current) ro.observe(panelRef.current.firstElementChild ?? panelRef.current);
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => {
      ro?.disconnect();
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [open, place]);

  const enter = (e: ReactPointerEvent) => {
    if (e.pointerType !== 'mouse') return;
    if (open) clear();
    else later(() => setOpen(true), OPEN_DELAY_MS);
  };
  const leave = (e: ReactPointerEvent) => {
    if (e.pointerType !== 'mouse') return;
    later(() => setOpen(false), CLOSE_DELAY_MS);
  };
  const inside = (node: EventTarget | null) =>
    !!node && (triggerRef.current?.contains(node as Node) || panelRef.current?.contains(node as Node));
  const focusIn = (e: FocusEvent) => {
    // Keyboard focus only: a mouse press on a link also focuses it, and that
    // press is about to open the page, not this card. The toggle opens the
    // card on its own press, so focus arriving on it must not open it first.
    if ((e.target as HTMLElement).closest('[data-popover-toggle]')) return;
    let keyboard = true;
    try {
      keyboard = (e.target as HTMLElement).matches(':focus-visible');
    } catch {
      /* older engines: treat as keyboard */
    }
    if (keyboard) {
      clear();
      setOpen(true);
    }
  };
  const focusOut = (e: FocusEvent) => {
    if (!inside(e.relatedTarget)) setOpen(false);
  };

  const toggle = useCallback(() => {
    clear();
    setOpen((o) => !o);
  }, []);

  return (
    <PopoverContext.Provider value={{ open, toggle, panelId }}>
      <span
        ref={triggerRef}
        className={`inline-flex min-w-0 ${className}`}
        onPointerEnter={enter}
        onPointerLeave={leave}
        onFocus={focusIn}
        onBlur={focusOut}
      >
        {children}
      </span>
      {open &&
        createPortal(
          <div
            ref={panelRef}
            id={panelId}
            role="dialog"
            aria-label={label}
            onPointerEnter={enter}
            onPointerLeave={leave}
            onBlur={focusOut}
            className="fixed z-[100] border bg-[var(--bg-raised)] text-left text-[13.5px] leading-snug text-[var(--text-sec)]"
            style={{
              borderColor: 'var(--rule-bright)',
              top: pos?.top ?? 0,
              left: pos?.left ?? 0,
              width: pos?.width ?? width,
              maxHeight: pos?.maxHeight,
              overflowY: pos?.maxHeight ? 'auto' : undefined,
              // Drawn once to be measured, then shown where it fits.
              visibility: pos ? 'visible' : 'hidden',
            }}
          >
            {content}
          </div>,
          document.body,
        )}
    </PopoverContext.Provider>
  );
}

/**
 * A control inside a Popover's trigger that opens and closes the card on press.
 * This is how a touch screen reaches it, since there is no hover. Put it on the
 * part of the trigger that is NOT the link (ObjectRef puts it on the kind mark).
 */
export function PopoverToggle({ children, label, className = '' }: { children: ReactNode; label: string; className?: string }) {
  const ctx = useContext(PopoverContext);
  if (!ctx) return <>{children}</>;
  return (
    <button
      type="button"
      onClick={ctx.toggle}
      aria-expanded={ctx.open}
      aria-controls={ctx.open ? ctx.panelId : undefined}
      aria-label={label}
      data-popover-toggle=""
      className={`inline-flex items-center ${FOCUS_RING} ${className}`}
    >
      {children}
    </button>
  );
}

/* ── PopoverRows ────────────────────────────────────────────────────────── */

/**
 * Where a step or part stands, drawn with the design system's fill rule:
 * filled = present or done, half = partly, open = not yet. No colour — the
 * shape carries it, and the word is there for screen readers.
 */
export type PopoverRowState = 'done' | 'partial' | 'open';

const STATE_WORD: Record<PopoverRowState, string> = {
  done: 'Done',
  partial: 'Partly done',
  open: 'Not yet',
};

export function StateMark({ state }: { state: PopoverRowState }) {
  return (
    <span className="inline-flex shrink-0 items-center text-[var(--text-ter)]">
      <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden>
        <rect x="0.5" y="0.5" width="9" height="9" fill="none" stroke="currentColor" />
        {state === 'done' && <rect x="0.5" y="0.5" width="9" height="9" fill="currentColor" />}
        {state === 'partial' && <rect x="0.5" y="0.5" width="4.5" height="9" fill="currentColor" />}
      </svg>
      <span className="sr-only">{STATE_WORD[state]}</span>
    </span>
  );
}

export interface PopoverRow {
  /** A stable name for the row, so a caller can pick one out (the track story reads these). */
  id?: string;
  label: ReactNode;
  value: ReactNode;
  /** Optional where-it-stands mark drawn before the label. */
  state?: PopoverRowState;
  /** A second, quieter line under the value. */
  note?: ReactNode;
}

export interface PopoverRowsProps {
  /** Mono eyebrow at the top of the card ("SESSION · HOW IT GOT HERE"). */
  heading?: ReactNode;
  rows: PopoverRow[];
  /** One line under the rows, before the link. */
  footnote?: ReactNode;
  /** "Open the session" — the page the card summarises. */
  link?: { label: string; href: string };
}

/** Label/value rows with an optional link at the foot, so every summary card reads alike. */
export function PopoverRows({ heading, rows, footnote, link }: PopoverRowsProps) {
  return (
    <div>
      {heading && (
        <div className={`border-b px-3.5 py-2 ${LABEL_MONO}`} style={{ borderColor: 'var(--rule)' }}>
          {heading}
        </div>
      )}
      {/* One grid for every row, so the values line up under each other. */}
      <dl className="grid grid-cols-[auto_1fr] px-3.5 py-1">
        {rows.map((r, i) => {
          const rule = i < rows.length - 1 ? 'border-b' : '';
          return (
            <Fragment key={i}>
              <dt
                className={`flex items-baseline gap-1.5 whitespace-nowrap py-2 pr-3 font-mono text-[12px] uppercase tracking-[0.08em] text-[var(--text-ter)] ${rule}`}
                style={{ borderColor: 'var(--rule)' }}
              >
                {r.state && <StateMark state={r.state} />}
                <span>{r.label}</span>
              </dt>
              <dd className={`min-w-0 py-2 text-[var(--text-pri)] ${rule}`} style={{ borderColor: 'var(--rule)' }}>
                {r.value}
                {r.note && <div className="mt-0.5 text-[12.5px] text-[var(--text-dim)]">{r.note}</div>}
              </dd>
            </Fragment>
          );
        })}
      </dl>
      {(footnote || link) && (
        <div className="flex flex-wrap items-baseline justify-between gap-2 border-t px-3.5 py-2" style={{ borderColor: 'var(--rule)' }}>
          {footnote && <span className="text-[12.5px] text-[var(--text-dim)]">{footnote}</span>}
          {link && (
            <Link
              to={link.href}
              className={`ml-auto font-mono text-[12px] uppercase tracking-[0.08em] text-[var(--text-pri)] underline decoration-[var(--rule-bright)] underline-offset-[3px] hover:decoration-solid ${FOCUS_RING}`}
            >
              {link.label} →
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
